import { useMemo, useState } from 'react';

import { SidebarContext } from './SidebarContext';
import type { SidebarState, SidebarVariant } from './types';

interface SidebarProviderProps {
  children: React.ReactNode;
  initialState?: SidebarState;
  variant?: SidebarVariant;
  isCollapsible?: boolean;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children,
  initialState = 'expanded',
  variant = 'collapsible',
  isCollapsible = true,
}) => {
  const [state, setState] = useState<SidebarState>(initialState);

  const finalState = useMemo(() => {
    switch (variant) {
      case 'collapsible':
        return ['expanded', 'collapsed'].includes(state) ? state : 'expanded';
      case 'overlay':
        return ['visible', 'hidden'].includes(state) ? state : 'hidden';
    }
  }, [state, variant]);

  const toggle = () => {
    if (variant == 'collapsible') {
      if (!isCollapsible) return;
      setState(() => (finalState === 'collapsed' ? 'expanded' : 'collapsed'));
    } else if (variant == 'overlay') {
      setState(() => (finalState === 'visible' ? 'hidden' : 'visible'));
    }
  };

  const setSidebarState = (newState: SidebarState) => {
    setState(newState);
  };

  return (
    <SidebarContext.Provider
      value={{
        state: finalState,
        toggle,
        setSidebarState,
        variant,
        isCollapsible,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
